import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getOIAlertsStatus,
  getOIAlertsTriggers,
  getOIAlertsSnapshot,
  startOIAlerts,
  stopOIAlerts,
  saveOIAlert,
  deleteOIAlert,
  getOrderbookStatus,
  getOrderbookTracked,
  saveOrderbookAlert,
  deleteOrderbookAlert,
  startOrderbook,
  stopOrderbook,
  getMarkPriceStatus,
  getMarkPriceTriggers,
  saveMarkPriceAlert,
  deleteMarkPriceAlert,
  startMarkPrice,
  stopMarkPrice,
  getFundingStatus,
  saveFundingAlert,
  deleteFundingAlert,
  startFunding,
  stopFunding,
  getPriceDifferenceStatus,
  getPriceDifferenceSettings,
  savePriceDifferenceAlert,
  deletePriceDifferenceAlert,
  startPriceDifference,
  stopPriceDifference,
  getSplashStatus,
  getSplashSettings,
  saveSplashAlert,
  deleteSplashAlert,
  updateSplashAlertsIgnore,
  startSplash,
  stopSplash,
  getSpreadStatus,
  getSpreadSettings,
  saveSpreadAlert,
  deleteSpreadAlert,
  updateSpreadAlertsIgnore,
  startSpread,
  stopSpread,
  getAlertsOverview,
  getAlertsOverviewFull,
  updateOIAlertsIgnore,
  updateOrderbookAlertsIgnore,
  updateMarkPriceAlertsIgnore,
  updatePriceDifferenceAlertsIgnore,
} from '@/lib/api'
import { toast } from '@/hooks/use-toast'
import type { Alert, OrderbookAlert, MarkPriceAlert, FundingAlert, PriceDifferenceAlert, SplashAlert, SpreadAlert } from '@/types'

// OI Alerts hooks
export function useOIAlertsStatus() {
  return useQuery({
    queryKey: ['alerts', 'oi', 'status'],
    queryFn: getOIAlertsStatus,
    refetchInterval: 5000,
  })
}

export function useOIAlertsTriggers() {
  return useQuery({
    queryKey: ['alerts', 'oi', 'triggers'],
    queryFn: getOIAlertsTriggers,
    refetchInterval: 5000,
  })
}

export function useOIAlertsSnapshot() {
  return useQuery({
    queryKey: ['alerts', 'oi', 'snapshot'],
    queryFn: getOIAlertsSnapshot,
    refetchInterval: 5000,
  })
}

export function useStartOIAlerts() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: startOIAlerts,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts', 'oi'] })
    },
    onError: () => {
      toast({
        title: 'Cannot start alerts',
        description: 'Alerts are managed by the process manager',
        variant: 'destructive',
      })
    },
  })
}

export function useStopOIAlerts() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: stopOIAlerts,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts', 'oi'] })
    },
    onError: () => {
      toast({
        title: 'Cannot stop alerts',
        description: 'Alerts are managed by the process manager',
        variant: 'destructive',
      })
    },
  })
}

export function useSaveOIAlert() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (alert: Partial<Alert>) => saveOIAlert(alert),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts', 'oi'] })
    },
  })
}

export function useDeleteOIAlert() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deleteOIAlert(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts', 'oi'] })
    },
  })
}

export function useUpdateOIAlertsIgnore() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (ignore: string[]) => updateOIAlertsIgnore(ignore),
    onSuccess: async () => {
      // Invalidate all OI alert queries including status (which contains ignore list)
      await queryClient.invalidateQueries({ queryKey: ['alerts', 'oi'] })
      // Also invalidate global settings in case it's being read there
      await queryClient.invalidateQueries({ queryKey: ['settings', 'global'] })
    },
  })
}

// Orderbook hooks
export function useOrderbookStatus() {
  return useQuery({
    queryKey: ['alerts', 'orderbook', 'status'],
    queryFn: getOrderbookStatus,
    refetchInterval: 5000,
  })
}

export function useOrderbookTracked() {
  return useQuery({
    queryKey: ['orderbook', 'tracked'],
    queryFn: getOrderbookTracked,
    refetchInterval: 30000, // Slowed to 30s — WS push handles real-time updates
  })
}

export function useSaveOrderbookAlert() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (alert: Partial<OrderbookAlert>) => saveOrderbookAlert(alert),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts', 'orderbook'] })
    },
  })
}

export function useDeleteOrderbookAlert() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deleteOrderbookAlert(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts', 'orderbook'] })
    },
  })
}

export function useUpdateOrderbookAlertsIgnore() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (ignore: string[]) => updateOrderbookAlertsIgnore(ignore),
    onSuccess: async () => {
      // Invalidate all orderbook queries including status (which contains ignore list)
      await queryClient.invalidateQueries({ queryKey: ['alerts', 'orderbook'] })
      // Also invalidate global settings which contains orderbook_alerts_ignore
      await queryClient.invalidateQueries({ queryKey: ['settings', 'global'] })
    },
  })
}

export function useStartOrderbook() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: startOrderbook,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts', 'orderbook'] })
    },
    onError: () => {
      toast({
        title: 'Cannot start orderbook alerts',
        description: 'Alerts are managed by the process manager',
        variant: 'destructive',
      })
    },
  })
}

export function useStopOrderbook() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: stopOrderbook,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts', 'orderbook'] })
    },
    onError: () => {
      toast({
        title: 'Cannot stop orderbook alerts',
        description: 'Alerts are managed by the process manager',
        variant: 'destructive',
      })
    },
  })
}

// Mark Price hooks
export function useMarkPriceStatus() {
  return useQuery({
    queryKey: ['alerts', 'markprice', 'status'],
    queryFn: getMarkPriceStatus,
    refetchInterval: 5000,
  })
}

export function useMarkPriceTriggers() {
  return useQuery({
    queryKey: ['alerts', 'markprice', 'triggers'],
    queryFn: getMarkPriceTriggers,
    refetchInterval: 5000,
  })
}

export function useSaveMarkPriceAlert() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (alert: Partial<MarkPriceAlert>) => saveMarkPriceAlert(alert),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts', 'markprice'] })
    },
  })
}

export function useDeleteMarkPriceAlert() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deleteMarkPriceAlert(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts', 'markprice'] })
    },
  })
}

export function useUpdateMarkPriceAlertsIgnore() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (ignore: string[]) => updateMarkPriceAlertsIgnore(ignore),
    onSuccess: async () => {
      // Invalidate all mark price queries including status (which contains ignore list)
      await queryClient.invalidateQueries({ queryKey: ['alerts', 'markprice'] })
      // Also invalidate global settings in case it's being read there
      await queryClient.invalidateQueries({ queryKey: ['settings', 'global'] })
    },
  })
}

export function useStartMarkPrice() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: startMarkPrice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts', 'markprice'] })
    },
    onError: () => {
      toast({
        title: 'Cannot start mark price alerts',
        description: 'Alerts are managed by the process manager',
        variant: 'destructive',
      })
    },
  })
}

export function useStopMarkPrice() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: stopMarkPrice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts', 'markprice'] })
    },
    onError: () => {
      toast({
        title: 'Cannot stop mark price alerts',
        description: 'Alerts are managed by the process manager',
        variant: 'destructive',
      })
    },
  })
}

// Funding hooks
export function useFundingStatus() {
  return useQuery({
    queryKey: ['alerts', 'funding', 'status'],
    queryFn: getFundingStatus,
    refetchInterval: 5000,
  })
}

export function useSaveFundingAlert() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (alert: Partial<FundingAlert>) => saveFundingAlert(alert),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts', 'funding'] })
    },
  })
}

export function useDeleteFundingAlert() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deleteFundingAlert(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts', 'funding'] })
    },
  })
}

export function useStartFunding() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: startFunding,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts', 'funding'] })
    },
    onError: () => {
      toast({
        title: 'Cannot start funding alerts',
        description: 'Alerts are managed by the process manager',
        variant: 'destructive',
      })
    },
  })
}

export function useStopFunding() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: stopFunding,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts', 'funding'] })
    },
    onError: () => {
      toast({
        title: 'Cannot stop funding alerts',
        description: 'Alerts are managed by the process manager',
        variant: 'destructive',
      })
    },
  })
}

// Price Difference hooks
export function usePriceDifferenceStatus() {
  return useQuery({
    queryKey: ['alerts', 'pricediff', 'status'],
    queryFn: getPriceDifferenceStatus,
    refetchInterval: 5000,
  })
}

export function usePriceDifferenceSettings() {
  return useQuery({
    queryKey: ['alerts', 'pricediff', 'settings'],
    queryFn: getPriceDifferenceSettings,
    refetchInterval: 5000,
  })
}

export function useSavePriceDifferenceAlert() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (alert: Partial<PriceDifferenceAlert>) => savePriceDifferenceAlert(alert),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts', 'pricediff'] })
    },
  })
}

export function useDeletePriceDifferenceAlert() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deletePriceDifferenceAlert(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts', 'pricediff'] })
    },
  })
}

export function useUpdatePriceDifferenceAlertsIgnore() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (ignore: string[]) => updatePriceDifferenceAlertsIgnore(ignore),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts', 'pricediff'] })
    },
  })
}

export function useStartPriceDifference() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: startPriceDifference,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts', 'pricediff'] })
    },
    onError: () => {
      toast({
        title: 'Cannot start price difference alerts',
        description: 'Alerts are managed by the process manager',
        variant: 'destructive',
      })
    },
  })
}

export function useStopPriceDifference() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: stopPriceDifference,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts', 'pricediff'] })
    },
    onError: () => {
      toast({
        title: 'Cannot stop price difference alerts',
        description: 'Alerts are managed by the process manager',
        variant: 'destructive',
      })
    },
  })
}

// Splash hooks
export function useSplashStatus() {
  return useQuery({
    queryKey: ['alerts', 'splash', 'status'],
    queryFn: getSplashStatus,
    refetchInterval: 5000,
  })
}

export function useSplashSettings() {
  return useQuery({
    queryKey: ['alerts', 'splash', 'settings'],
    queryFn: getSplashSettings,
    refetchInterval: 5000,
  })
}

export function useSaveSplashAlert() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (alert: Partial<SplashAlert>) => saveSplashAlert(alert),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts', 'splash'] })
    },
  })
}

export function useDeleteSplashAlert() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deleteSplashAlert(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts', 'splash'] })
    },
  })
}

export function useUpdateSplashAlertsIgnore() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (ignore: string[]) => updateSplashAlertsIgnore(ignore),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['alerts', 'splash'] })
      await queryClient.invalidateQueries({ queryKey: ['settings', 'global'] })
    },
  })
}

export function useStartSplash() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: startSplash,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts', 'splash'] })
    },
    onError: () => {
      toast({
        title: 'Cannot start splash alerts',
        description: 'Alerts are managed by the process manager',
        variant: 'destructive',
      })
    },
  })
}

export function useStopSplash() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: stopSplash,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts', 'splash'] })
    },
    onError: () => {
      toast({
        title: 'Cannot stop splash alerts',
        description: 'Alerts are managed by the process manager',
        variant: 'destructive',
      })
    },
  })
}

// Spread hooks
export function useSpreadStatus() {
  return useQuery({
    queryKey: ['alerts', 'spread', 'status'],
    queryFn: getSpreadStatus,
    refetchInterval: 5000,
  })
}

export function useSpreadSettings() {
  return useQuery({
    queryKey: ['alerts', 'spread', 'settings'],
    queryFn: getSpreadSettings,
    refetchInterval: 5000,
  })
}

export function useSaveSpreadAlert() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (alert: Partial<SpreadAlert>) => saveSpreadAlert(alert),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts', 'spread'] })
    },
  })
}

export function useDeleteSpreadAlert() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deleteSpreadAlert(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts', 'spread'] })
    },
  })
}

export function useUpdateSpreadAlertsIgnore() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (ignore: string[]) => updateSpreadAlertsIgnore(ignore),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['alerts', 'spread'] })
      await queryClient.invalidateQueries({ queryKey: ['settings', 'global'] })
    },
  })
}

export function useStartSpread() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: startSpread,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts', 'spread'] })
    },
    onError: () => {
      toast({
        title: 'Cannot start spread alerts',
        description: 'Alerts are managed by the process manager',
        variant: 'destructive',
      })
    },
  })
}

export function useStopSpread() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: stopSpread,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts', 'spread'] })
    },
    onError: () => {
      toast({
        title: 'Cannot stop spread alerts',
        description: 'Alerts are managed by the process manager',
        variant: 'destructive',
      })
    },
  })
}

// Overview/Dashboard hook
export function useAlertsOverview() {
  return useQuery({
    queryKey: ['alerts', 'overview'],
    queryFn: getAlertsOverview,
    refetchInterval: 30000, // Slowed to 30s — WS push handles real-time updates
  })
}

// Full Alerts Overview hook (uses /api/alerts/overview with filters)
export function useAlertsOverviewFull(params: {
  exchange?: string
  alert_type?: string
  tags?: string
  time_range?: string
  alert_age?: string
  alert_names?: string
  market_type?: string
  min_value?: number
  max_value?: number
} = {}, options: { enabled?: boolean; refetchInterval?: number } = {}) {
  return useQuery({
    queryKey: ['alerts', 'overview', 'full', params],
    queryFn: () => getAlertsOverviewFull(params),
    enabled: options.enabled ?? true,
    refetchInterval: options.refetchInterval ?? 30000, // Slowed to 30s — WS push handles real-time
  })
}

